/** biome-ignore-all lint/complexity/noExcessiveCognitiveComplexity: TODO */
import Decimal from 'decimal.js';
import type { TowersResultCacheDto } from '../api';
import {
  HOUSE_EDGE,
  PATTERN_COLS,
  PATTERN_MAP,
  PATTERN_MINES,
  type Pattern,
} from '../constants';
import type { TowersBrick } from '../state';

/**
 * Finds the last row and column picked from tower results and determines if it was a win or loss
 * @param towersResults - 2D array of TowersBrick representing the game grid
 * @returns Object containing lastRow, lastColumn, and isWin status
 */
export const findLastPickedResult = (towersResults: TowersBrick[][]) => {
  if (!towersResults || towersResults.length === 0) {
    return { lastRow: null, lastColumn: null, isWin: null };
  }

  // Find the last picked brick by iterating through rows from bottom to top
  // (since rows are added from bottom up in the game)
  let lastPickedBrick: TowersBrick | null = null;
  let lastRow: number | null = null;
  let lastColumn: number | null = null;

  // Start from the most recent row (highest index) and work backwards
  for (let rowIndex = towersResults.length - 1; rowIndex >= 0; rowIndex--) {
    const row = towersResults[rowIndex];
    if (!row) {
      continue;
    }

    // Check each column in this row for a picked brick
    for (let colIndex = 0; colIndex < row.length; colIndex++) {
      const brick = row[colIndex];
      if (brick?.isPicked) {
        lastPickedBrick = brick;
        lastRow = rowIndex;
        lastColumn = colIndex;
        break; // Found the most recent pick
      }
    }

    // If we found a picked brick in this row, stop searching
    if (lastPickedBrick) {
      break;
    }
  }

  if (!lastPickedBrick) {
    return { lastRow: null, lastColumn: null, isWin: null };
  }

  // Determine if it was a win or loss based on the brick type
  // type = "BRICK" means it was a safe pick (win)
  // type = "MINE" means it was a mine (loss)
  const isWin = lastPickedBrick.type === 'BRICK';
  const isLoss = lastPickedBrick.type === 'MINE';

  if (isWin) {
    return {
      lastRow,
      lastColumn,
      isWin: true,
    };
  }

  if (isLoss) {
    return {
      lastRow,
      lastColumn,
      isWin: false,
    };
  }

  return {
    lastRow,
    lastColumn,
    isWin: null,
  };
};

/**
 * Gets all picked positions from tower results ordered by row (most recent first)
 * @param towersResults - Array of tower results from the API
 * @returns Array of picked positions with their win/loss status
 */
export const getAllPickedResults = (
  towersResults: TowersResultCacheDto[] | null | undefined
) => {
  if (!towersResults || towersResults.length === 0) {
    return [];
  }

  return towersResults
    .filter((result) => result.isPicked === true)
    .sort((a, b) => (b.row ?? 0) - (a.row ?? 0)) // Sort by row descending (most recent first)
    .map((result) => ({
      row: result.row ?? null,
      column: result.column ?? null,
      isWin: result.isBrick === true,
      isLoss: result.isMine === true,
      isBrick: result.isBrick ?? false,
      isMine: result.isMine ?? false,
    }));
};

/**
 * Calculate the multiplier based on the number of rows cleared and game pattern
 * @param picks - Number of picks (rows successfully cleared)
 * @param pattern - Game pattern (difficulty level)
 * @returns Multiplier value
 */
export const calculateMultiplier = (
  picks: number,
  pattern: Pattern
): number => {
  // Backend formula: (1 - HOUSE_EDGE) * Math.Pow(ratio, picks)
  // where ratio = columns / (columns - mines)

  const maxRows = PATTERN_MAP[pattern].rows;

  if (picks < 0 || picks > maxRows) {
    return 0;
  }

  // If no picks, multiplier is 1
  if (picks === 0) {
    return 1;
  }

  const columns = PATTERN_COLS[pattern];
  const mines = PATTERN_MINES[pattern];

  // Safety check: columns must be greater than mines
  if (columns <= mines) {
    throw new Error('Total columns must be greater than the number of mines');
  }

  // Use Decimal.js for precise calculations
  const ratio = new Decimal(columns).div(new Decimal(columns).minus(mines));
  const multiplier = new Decimal(1)
    .minus(new Decimal(HOUSE_EDGE))
    .mul(ratio.pow(picks));

  return multiplier.toNumber();
};

/**
 * Count the number of rows cleared from tower results
 * @param towersResults - 2D array of TowersBrick representing the game grid
 * @returns Number of rows with picked bricks
 */
export const countRowsCleared = (towersResults: TowersBrick[][]): number => {
  if (!towersResults || towersResults.length === 0) {
    return 0;
  }

  return towersResults.filter((row) =>
    row?.some((brick) => brick?.isPicked && brick.type === 'BRICK')
  ).length;
};
